import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon, Close as CloseIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import quotePackageService from '../services/quotePackageService';

const SECTION_OPTIONS = [
  { value: 'cover', label: 'Cover' },
  { value: 'analysis', label: 'Analysis' },
  { value: 'details', label: 'Details' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'terms', label: 'Terms' },
  { value: 'custom', label: 'Custom' },
];

const defaultSection = (sort) => ({
  section_type: 'cover',
  title: '',
  content: '',
  sort_order: sort ?? 0,
  page_number: 1,
});

const QuotePackageSectionsDialog = ({ open, onClose, quotePackage, onChange }) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editSectionId, setEditSectionId] = useState(null);
  const [form, setForm] = useState(defaultSection(0));
  const [saving, setSaving] = useState(false);
  const [expandedPages, setExpandedPages] = useState({ 1: true, 2: true, 3: true, 4: true });

  useEffect(() => {
    if (!open || !quotePackage) return;
    const fetchSections = async () => {
      setLoading(true);
      const response = await quotePackageService.getSections(quotePackage.id);
      if (response.success) {
        setSections(response.data || []);
      } else {
        setError(response.error);
      }
      setLoading(false);
    };
    fetchSections();
  }, [open, quotePackage]);

  useEffect(() => {
    if (!open) {
      setSections([]);
      setForm(defaultSection(0));
      setEditSectionId(null);
      setError('');
      setExpandedPages({ 1: true, 2: true, 3: true, 4: true });
    }
  }, [open]);

  // Auto-update sort_order when section_type or page_number changes (only for new sections)
  useEffect(() => {
    if (!editSectionId && form.section_type && form.page_number) {
      const sectionsOnPage = sections.filter((s) => (s.page_number || 1) === form.page_number);
      const nextSort = sectionsOnPage.length > 0 
        ? Math.max(...sectionsOnPage.map((s) => s.sort_order || 0)) + 1 
        : 0;
      if (form.sort_order !== nextSort) {
        setForm((prev) => ({ ...prev, sort_order: nextSort }));
      }
    }
  }, [form.section_type, form.page_number, sections, editSectionId]);

  const sortedSections = useMemo(() => {
    return [...sections].sort((a, b) => {
      if ((a.page_number || 1) !== (b.page_number || 1)) {
        return (a.page_number || 1) - (b.page_number || 1);
      }
      return (a.sort_order || 0) - (b.sort_order || 0);
    });
  }, [sections]);

  const sectionsByPage = useMemo(() => {
    const grouped = {};
    sortedSections.forEach((section) => {
      const page = section.page_number || 1;
      if (!grouped[page]) {
        grouped[page] = [];
      }
      grouped[page].push(section);
    });
    return grouped;
  }, [sortedSections]);

  const getPageLabel = (pageNumber) => {
    const pageSections = sectionsByPage[pageNumber] || [];
    if (pageSections.length === 0) return `Σελίδα ${pageNumber}`;
    const firstType = pageSections[0].section_type;
    if (firstType === 'cover') return 'Σελίδα 1 - Cover';
    if (firstType === 'analysis') return 'Σελίδα 2 - Analysis';
    if (firstType === 'details' || firstType === 'pricing') return 'Σελίδα 3 - Pricing & Details';
    if (firstType === 'terms') return 'Σελίδα 4 - Terms';
    return `Σελίδα ${pageNumber}`;
  };

  const handleTogglePage = (pageNumber) => (event, isExpanded) => {
    setExpandedPages((prev) => ({
      ...prev,
      [pageNumber]: isExpanded,
    }));
  };

  const handleFormChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => {
      const updated = {
        ...prev,
        [field]: field === 'page_number' || field === 'sort_order' ? Number(value) : value,
      };
      // Auto-set page_number based on section type (only for fixed page types)
      if (field === 'section_type') {
        // For custom sections, preserve the existing page_number (don't auto-set)
        if (value === 'custom') {
          // Keep the existing page_number, or default to 1 if it's a new section
          if (!prev.page_number || prev.page_number < 1) {
            updated.page_number = 1;
          }
          // Auto-calculate sort_order for the current page (only if creating new section)
          if (!editSectionId) {
            const currentPage = updated.page_number || 1;
            const sectionsOnPage = sections.filter((s) => (s.page_number || 1) === currentPage);
            updated.sort_order = sectionsOnPage.length > 0 
              ? Math.max(...sectionsOnPage.map((s) => s.sort_order || 0)) + 1 
              : 0;
          }
        } else {
          // For fixed page types, auto-set the page_number
          let newPage = 1;
          if (value === 'cover') {
            newPage = 1;
          } else if (value === 'analysis') {
            newPage = 2;
          } else if (value === 'details' || value === 'pricing') {
            newPage = 3;
          } else if (value === 'terms') {
            newPage = 4;
          }
          updated.page_number = newPage;
          // Auto-calculate sort_order for the new page (only if creating new section)
          if (!editSectionId) {
            const sectionsOnPage = sections.filter((s) => (s.page_number || 1) === newPage);
            updated.sort_order = sectionsOnPage.length > 0 
              ? Math.max(...sectionsOnPage.map((s) => s.sort_order || 0)) + 1 
              : 0;
          }
        }
      }
      // Auto-calculate sort_order when page_number changes (only if creating new section)
      if (field === 'page_number' && !editSectionId) {
        const newPage = Number(value);
        const sectionsOnPage = sections.filter((s) => (s.page_number || 1) === newPage);
        updated.sort_order = sectionsOnPage.length > 0 
          ? Math.max(...sectionsOnPage.map((s) => s.sort_order || 0)) + 1 
          : 0;
      }
      return updated;
    });
  };

  const resetForm = () => {
    // Calculate next sort_order for the current page
    const currentPage = form.page_number || 1;
    const sectionsOnPage = sections.filter((s) => (s.page_number || 1) === currentPage);
    const nextSort = sectionsOnPage.length > 0 
      ? Math.max(...sectionsOnPage.map((s) => s.sort_order || 0)) + 1 
      : 0;
    setForm(defaultSection(nextSort));
    setEditSectionId(null);
  };

  const handleEdit = (section) => {
    setForm({
      section_type: section.section_type,
      title: section.title || '',
      content: section.content || '',
      sort_order: section.sort_order ?? 0,
      page_number: section.page_number ?? 1,
    });
    setEditSectionId(section.id);
  };

  const handleDelete = async (sectionId) => {
    if (!quotePackage) return;
    setSaving(true);
    const response = await quotePackageService.deleteSection(quotePackage.id, sectionId);
    setSaving(false);
    if (response.success) {
      const refreshed = await quotePackageService.getSections(quotePackage.id);
      if (refreshed.success) {
        setSections(refreshed.data || []);
        if (onChange) onChange();
      }
    } else {
      setError(response.error);
    }
  };

  const handleSubmit = async () => {
    if (!quotePackage) return;
    // Ensure cover sections are always on page 1, analysis sections on page 2
    // Details and Pricing sections are always on page 3
    // Terms sections are always on page 4
    // Custom sections can be on any page (use the selected page_number)
    let pageNumber = Number.isFinite(form.page_number) ? form.page_number : 1;
    if (form.section_type === 'cover') {
      pageNumber = 1;
    } else if (form.section_type === 'analysis') {
      pageNumber = 2;
    } else if (form.section_type === 'details' || form.section_type === 'pricing') {
      pageNumber = 3;
    } else if (form.section_type === 'terms') {
      pageNumber = 4;
    }
    // For custom sections, pageNumber already has the value from form.page_number
    const payload = {
      section_type: form.section_type,
      title: form.title,
      content: form.content,
      sort_order: Number.isFinite(form.sort_order) ? form.sort_order : 0,
      page_number: pageNumber,
    };

    setSaving(true);
    const response = editSectionId
      ? await quotePackageService.updateSection(quotePackage.id, editSectionId, payload)
      : await quotePackageService.createSection(quotePackage.id, payload);
    setSaving(false);

    if (response.success) {
      const refreshed = await quotePackageService.getSections(quotePackage.id);
      if (refreshed.success) {
        setSections(refreshed.data || []);
        if (onChange) onChange();
      }
      resetForm();
    } else {
      setError(response.error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography component="span" variant="h6">Διαχείριση Ενοτήτων</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {quotePackage?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Προσθέστε ενότητες για το template της προσφοράς (cover, analysis, pricing κ.λπ.).
          </Typography>
        </Box>

        <Box sx={{ mb: 3, p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            {editSectionId ? 'Επεξεργασία Ενότητας' : 'Νέα Ενότητα'}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Select
                fullWidth
                size="small"
                value={form.section_type}
                onChange={handleFormChange('section_type')}
              >
                {SECTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                label="Τίτλος"
                value={form.title}
                onChange={handleFormChange('title')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={
                  form.section_type === 'cover' 
                    ? 'Σελίδα (Cover - Page 1)' 
                    : form.section_type === 'analysis'
                    ? 'Σελίδα (Analysis - Page 2)'
                    : form.section_type === 'details' || form.section_type === 'pricing'
                    ? 'Σελίδα (Page 3)'
                    : form.section_type === 'terms'
                    ? 'Σελίδα (Terms - Page 4)'
                    : 'Σελίδα'
                }
                value={form.page_number}
                onChange={handleFormChange('page_number')}
                inputProps={{ min: 1 }}
                disabled={form.section_type === 'cover' || form.section_type === 'analysis' || form.section_type === 'details' || form.section_type === 'pricing' || form.section_type === 'terms'}
                helperText={
                  form.section_type === 'cover' 
                    ? 'Cover sections εμφανίζονται πάντα στη σελίδα 1'
                    : form.section_type === 'analysis'
                    ? 'Analysis sections εμφανίζονται πάντα στη σελίδα 2'
                    : form.section_type === 'details' || form.section_type === 'pricing'
                    ? 'Details και Pricing sections εμφανίζονται πάντα στη σελίδα 3'
                    : form.section_type === 'terms'
                    ? 'Terms sections εμφανίζονται πάντα στη σελίδα 4'
                    : ''
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Σειρά"
                value={form.sort_order}
                onChange={handleFormChange('sort_order')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Περιεχόμενο"
                value={form.content}
                onChange={handleFormChange('content')}
                placeholder="Γράψτε το περιεχόμενο της ενότητας (μπορείτε να συμπεριλάβετε placeholders όπως {client_name})"
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            {editSectionId && (
              <Button
                variant="outlined"
                onClick={resetForm}
                startIcon={<AddIcon />}
                disabled={saving}
              >
                Νέα Ενότητα
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleSubmit}
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {editSectionId ? 'Αποθήκευση' : 'Προσθήκη'}
            </Button>
          </Box>
        </Box>

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Υπάρχουσες Ενότητες
        </Typography>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Φόρτωση ενοτήτων...
          </Typography>
        ) : sortedSections.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Δεν έχουν προστεθεί ενότητες ακόμη.
          </Typography>
        ) : (
          Object.keys(sectionsByPage)
            .sort((a, b) => Number(a) - Number(b))
            .map((pageNumber) => {
              const pageSections = sectionsByPage[pageNumber];
              const pageNum = Number(pageNumber);
              const isExpanded = expandedPages[pageNum] !== false;
              const sectionCount = pageSections.length;
              
              return (
                <Accordion
                  key={pageNumber}
                  expanded={isExpanded}
                  onChange={handleTogglePage(pageNum)}
                  sx={{ mb: 1, '&:before': { display: 'none' } }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {getPageLabel(pageNum)}
                      </Typography>
                      <Chip label={`${sectionCount} ενότητα${sectionCount !== 1 ? 'τες' : ''}`} size="small" variant="outlined" />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {pageSections.map((section) => (
                        <Box
                          key={section.id}
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 2,
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                              <Chip label={section.section_type} size="small" color="primary" />
                              <Chip label={`Σειρά ${section.sort_order ?? 0}`} size="small" variant="outlined" />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Επεξεργασία">
                                <IconButton size="small" onClick={() => handleEdit(section)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Διαγραφή">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(section.id)}
                                  disabled={saving}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                          {section.title && (
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
                              {section.title}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ whiteSpace: 'pre-line', mt: 1 }}
                          >
                            {section.content || '-'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Κλείσιμο
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuotePackageSectionsDialog;

